import React from 'react';
import { TextField as MuiTextField, Typography } from '@mui/material';
import { BaseField } from './BaseField';
import type { BaseFieldProps } from './types';

/**
 * Text field component that handles:
 * - short_text (form-builder)
 * - text, email, tel, number (legacy)
 */
export const TextField: React.FC<BaseFieldProps> = ({
  field,
  value = '',
  onChange,
  onBlur,
  mode = 'edit',
  error,
  touched,
  sx,
  showLabel = true,
  disabled,
}) => {
  // Extract field properties based on field format
  const label = 'label' in field ? field.label : '';
  const required = 'required' in field ? field.required : false;
  
  // Determine input type
  let inputType = 'text';
  if ('type' in field) {
    if (field.type === 'email' || field.type === 'tel' || field.type === 'number') {
      inputType = field.type;
    }
  }
  if ('inputType' in field && field.inputType) {
    inputType = field.inputType;
  }

  // Extract placeholder
  const placeholder = 'placeholder' in field ? field.placeholder : label ? `Enter ${label.toLowerCase()}` : 'Enter text';

  // Handle display mode
  if (mode === 'display') {
    return (
      <BaseField field={field} mode={mode} sx={sx} showLabel={showLabel}>
        <Typography variant="body2">{value || '-'}</Typography>
      </BaseField>
    );
  }

  // Handle edit/preview modes
  return (
    <BaseField
      field={field}
      mode={mode}
      error={error}
      touched={touched}
      sx={sx}
      showLabel={false}
      disabled={disabled}
    >
      <MuiTextField
        fullWidth
        label={showLabel ? label : undefined}
        type={inputType}
        value={value}
        onChange={onChange ? (e) => onChange(e.target.value) : undefined}
        onBlur={onBlur}
        disabled={disabled || mode === 'preview'}
        required={required}
        error={!!(touched && error)}
        helperText={touched && error ? error : undefined}
        placeholder={placeholder}
        variant="outlined"
        size="small"
        slotProps={{
          htmlInput: {
            min: inputType === 'number' && 'validation' in field && field.validation ?
              (Array.isArray(field.validation)
                ? field.validation.find(v => v.type === 'number')?.min
                : field.validation.min
              ) : undefined,
            max: inputType === 'number' && 'validation' in field && field.validation ?
              (Array.isArray(field.validation)
                ? field.validation.find(v => v.type === 'number')?.max
                : field.validation.max
              ) : undefined,
            step: inputType === 'number' ? 'any' : undefined,
          },
        }}
      />
    </BaseField>
  );
};
